import { MESSAGE_CATALOG } from './message-catalog.generated';
const normalized = (value) => value.trim().replace(/\.+$/, '').toLowerCase();
const byId = new Map(MESSAGE_CATALOG.map(row => [row.id, row]));
const byText = new Map();
const templates = [];
MESSAGE_CATALOG.forEach(row => {
    [row.key, row.ru, row.en, ...(row.aliases || [])].forEach(text => { if (!byText.has(normalized(text)))
        byText.set(normalized(text), row); });
    if (row.template)
        for (const source of [row.ru, row.en]) {
            const names = [];
            const pattern = source.split(/(\{p\d+\})/).map(part => {
                if (/^\{p\d+\}$/.test(part)) {
                    names.push(part.slice(1, -1));
                    return '([\\s\\S]{0,2000}?)';
                }
                return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            }).join('');
            templates.push({ row, names, pattern: new RegExp('^' + pattern + '$') });
        }
});
templates.sort((a, b) => b.row.ru.length - a.row.ru.length);
export function messageInfo(id, params) {
    const row = byId.get(id) || byId.get(1500);
    const render = (text) => text.replace(/\{p\d+\}/g, key => { var _a; return (_a = params === null || params === void 0 ? void 0 : params[key.slice(1, -1)]) !== null && _a !== void 0 ? _a : key; });
    return { message_id: row.id, message_texts: { ru: render(row.ru), en: render(row.en) }, ...(params && Object.keys(params).length ? { params } : {}) };
}
function lookup(text) {
    const row = byText.get(normalized(text));
    if (row)
        return messageInfo(row.id);
    for (const { row, names, pattern } of templates) {
        const match = pattern.exec(text);
        if (match)
            return messageInfo(row.id, Object.fromEntries(names.map((name, i) => [name, match[i + 1]])));
    }
    return undefined;
}
export function errorStrings(value) {
    if (typeof value === 'string')
        return value.trim() ? [value] : [];
    if (Array.isArray(value))
        return value.flatMap(errorStrings);
    if (value && typeof value === 'object')
        return Object.entries(value).filter(([key]) => !['api_message', 'api_messages', 'message_id', 'message_texts', 'error_code', 'code', 'status'].includes(key)).flatMap(([, item]) => errorStrings(item));
    return [];
}
export function messageFor(status, payload) {
    const data = payload && typeof payload === 'object' ? payload : {};
    const metadata = data.api_message && typeof data.api_message === 'object' ? data.api_message : {};
    if (typeof metadata.id === 'number' && byId.has(metadata.id)) {
        const params = metadata.params && typeof metadata.params === 'object' ? Object.fromEntries(Object.entries(metadata.params).filter((pair) => /^p\d+$/.test(pair[0]) && typeof pair[1] === 'string')) : undefined;
        return messageInfo(metadata.id, params);
    }
    const state = String(data.status || '').toUpperCase();
    const failed = status >= 400 || data.success === false || data.ok === false || Boolean(data.error) || Boolean(data.errors && (Array.isArray(data.errors) ? data.errors.length : Object.keys(Object(data.errors)).length)) || ['FAILURE', 'FAILED', 'ERROR', 'TIMEOUT'].includes(state);
    if (failed) {
        const unique = [...new Set(['errors', 'error', 'detail', 'message', 'non_field_errors'].flatMap(key => errorStrings(data[key])))];
        const precise = unique.length === 1 ? lookup(unique[0]) : undefined;
        if (precise)
            return precise;
        const key = typeof data.error_code === 'string' ? data.error_code : typeof data.code === 'string' ? data.code : '';
        if (byText.has(normalized(key)))
            return messageInfo(byText.get(normalized(key)).id);
        if (state === 'TIMEOUT')
            return messageInfo(3004);
        if (status < 400 && ['FAILURE', 'FAILED', 'ERROR'].includes(state))
            return messageInfo(3003);
        return messageInfo(byId.has(1000 + status) ? 1000 + status : status >= 500 ? 1500 : 1400);
    }
    return messageInfo(status === 202 || ['PENDING', 'STARTED', 'PROCESSING', 'RETRY', 'QUEUED'].includes(state) ? 1002 : status === 201 ? 1001 : 1000);
}
// Existing Russian text is preserved; device locale never selects English.
export function russianMessage(value, fallback) {
    const parts = errorStrings(value).map(text => {
        var _a;
        if (/[а-яё]/i.test(text) && !/<\/?[a-z][^>]*>/i.test(text))
            return text;
        return ((_a = lookup(text)) === null || _a === void 0 ? void 0 : _a.message_texts.ru) || fallback;
    });
    return [...new Set(parts)].join(', ') || fallback;
}
export class CodedError extends Error {
    constructor(id, text) {
        const info = messageInfo(id);
        super(text || info.message_texts.ru);
        this.message_id = info.message_id;
        this.message_texts = info.message_texts;
    }
}
export { MESSAGE_CATALOG };
export function responseMessage(status, payload) {
    const info = messageFor(status, payload);
    return { api_message: { id: info.message_id, ...info.message_texts, ...(info.params ? { params: info.params } : {}) } };
}
